/**
 * ISO 3166-1 alpha-2 countries and their E.164 country calling codes.
 *
 * Reference DATA, not copy. The `name` column is the English ISO short name and
 * exists so a picker has something to render and search out of the box; it is
 * NOT a translated string and `packages/ui` still owns no user-facing copy —
 * `PhoneInput` takes a `countryNames` prop that overrides every one of them with
 * the consumer's already-translated names (the same contract every other string
 * in this package follows). Dial codes and ISO codes are locale-invariant.
 *
 * Deliberately no per-country national-number format rules: national numbering
 * plans change, a hand-rolled table of them goes stale silently, and validation
 * is the consumer's Zod schema's job. This module answers exactly two questions
 * — "which countries exist" and "what does one dial" — and nothing else.
 *
 * No dependency: `libphonenumber-js` is ~145 kB of metadata for a formatter this
 * product does not need, and it is not in the workspace today.
 */
/**
 * `[ISO 3166-1 alpha-2, E.164 country calling code (no `+`), English ISO short name]`.
 *
 * All 249 officially assigned alpha-2 codes, so a country is never missing from
 * the picker. `as const` is load-bearing: `CountryCode` below is derived from it,
 * which is what makes an unknown code a compile error rather than a runtime one.
 */
const COUNTRY_TABLE = [
    ['AD', '376', 'Andorra'],
    ['AE', '971', 'United Arab Emirates'],
    ['AF', '93', 'Afghanistan'],
    ['AG', '1', 'Antigua and Barbuda'],
    ['AI', '1', 'Anguilla'],
    ['AL', '355', 'Albania'],
    ['AM', '374', 'Armenia'],
    ['AO', '244', 'Angola'],
    ['AQ', '672', 'Antarctica'],
    ['AR', '54', 'Argentina'],
    ['AS', '1', 'American Samoa'],
    ['AT', '43', 'Austria'],
    ['AU', '61', 'Australia'],
    ['AW', '297', 'Aruba'],
    ['AX', '358', 'Åland Islands'],
    ['AZ', '994', 'Azerbaijan'],
    ['BA', '387', 'Bosnia and Herzegovina'],
    ['BB', '1', 'Barbados'],
    ['BD', '880', 'Bangladesh'],
    ['BE', '32', 'Belgium'],
    ['BF', '226', 'Burkina Faso'],
    ['BG', '359', 'Bulgaria'],
    ['BH', '973', 'Bahrain'],
    ['BI', '257', 'Burundi'],
    ['BJ', '229', 'Benin'],
    ['BL', '590', 'Saint Barthélemy'],
    ['BM', '1', 'Bermuda'],
    ['BN', '673', 'Brunei Darussalam'],
    ['BO', '591', 'Bolivia'],
    ['BQ', '599', 'Bonaire, Sint Eustatius and Saba'],
    ['BR', '55', 'Brazil'],
    ['BS', '1', 'Bahamas'],
    ['BT', '975', 'Bhutan'],
    ['BV', '47', 'Bouvet Island'],
    ['BW', '267', 'Botswana'],
    ['BY', '375', 'Belarus'],
    ['BZ', '501', 'Belize'],
    ['CA', '1', 'Canada'],
    ['CC', '61', 'Cocos (Keeling) Islands'],
    ['CD', '243', 'Congo, Democratic Republic of the'],
    ['CF', '236', 'Central African Republic'],
    ['CG', '242', 'Congo'],
    ['CH', '41', 'Switzerland'],
    ['CI', '225', "Côte d'Ivoire"],
    ['CK', '682', 'Cook Islands'],
    ['CL', '56', 'Chile'],
    ['CM', '237', 'Cameroon'],
    ['CN', '86', 'China'],
    ['CO', '57', 'Colombia'],
    ['CR', '506', 'Costa Rica'],
    ['CU', '53', 'Cuba'],
    ['CV', '238', 'Cabo Verde'],
    ['CW', '599', 'Curaçao'],
    ['CX', '61', 'Christmas Island'],
    ['CY', '357', 'Cyprus'],
    ['CZ', '420', 'Czechia'],
    ['DE', '49', 'Germany'],
    ['DJ', '253', 'Djibouti'],
    ['DK', '45', 'Denmark'],
    ['DM', '1', 'Dominica'],
    ['DO', '1', 'Dominican Republic'],
    ['DZ', '213', 'Algeria'],
    ['EC', '593', 'Ecuador'],
    ['EE', '372', 'Estonia'],
    ['EG', '20', 'Egypt'],
    ['EH', '212', 'Western Sahara'],
    ['ER', '291', 'Eritrea'],
    ['ES', '34', 'Spain'],
    ['ET', '251', 'Ethiopia'],
    ['FI', '358', 'Finland'],
    ['FJ', '679', 'Fiji'],
    ['FK', '500', 'Falkland Islands'],
    ['FM', '691', 'Micronesia'],
    ['FO', '298', 'Faroe Islands'],
    ['FR', '33', 'France'],
    ['GA', '241', 'Gabon'],
    ['GB', '44', 'United Kingdom'],
    ['GD', '1', 'Grenada'],
    ['GE', '995', 'Georgia'],
    ['GF', '594', 'French Guiana'],
    ['GG', '44', 'Guernsey'],
    ['GH', '233', 'Ghana'],
    ['GI', '350', 'Gibraltar'],
    ['GL', '299', 'Greenland'],
    ['GM', '220', 'Gambia'],
    ['GN', '224', 'Guinea'],
    ['GP', '590', 'Guadeloupe'],
    ['GQ', '240', 'Equatorial Guinea'],
    ['GR', '30', 'Greece'],
    ['GS', '500', 'South Georgia and the South Sandwich Islands'],
    ['GT', '502', 'Guatemala'],
    ['GU', '1', 'Guam'],
    ['GW', '245', 'Guinea-Bissau'],
    ['GY', '592', 'Guyana'],
    ['HK', '852', 'Hong Kong'],
    ['HM', '672', 'Heard Island and McDonald Islands'],
    ['HN', '504', 'Honduras'],
    ['HR', '385', 'Croatia'],
    ['HT', '509', 'Haiti'],
    ['HU', '36', 'Hungary'],
    ['ID', '62', 'Indonesia'],
    ['IE', '353', 'Ireland'],
    ['IL', '972', 'Israel'],
    ['IM', '44', 'Isle of Man'],
    ['IN', '91', 'India'],
    ['IO', '246', 'British Indian Ocean Territory'],
    ['IQ', '964', 'Iraq'],
    ['IR', '98', 'Iran'],
    ['IS', '354', 'Iceland'],
    ['IT', '39', 'Italy'],
    ['JE', '44', 'Jersey'],
    ['JM', '1', 'Jamaica'],
    ['JO', '962', 'Jordan'],
    ['JP', '81', 'Japan'],
    ['KE', '254', 'Kenya'],
    ['KG', '996', 'Kyrgyzstan'],
    ['KH', '855', 'Cambodia'],
    ['KI', '686', 'Kiribati'],
    ['KM', '269', 'Comoros'],
    ['KN', '1', 'Saint Kitts and Nevis'],
    ['KP', '850', "Korea, Democratic People's Republic of"],
    ['KR', '82', 'Korea, Republic of'],
    ['KW', '965', 'Kuwait'],
    ['KY', '1', 'Cayman Islands'],
    ['KZ', '7', 'Kazakhstan'],
    ['LA', '856', "Lao People's Democratic Republic"],
    ['LB', '961', 'Lebanon'],
    ['LC', '1', 'Saint Lucia'],
    ['LI', '423', 'Liechtenstein'],
    ['LK', '94', 'Sri Lanka'],
    ['LR', '231', 'Liberia'],
    ['LS', '266', 'Lesotho'],
    ['LT', '370', 'Lithuania'],
    ['LU', '352', 'Luxembourg'],
    ['LV', '371', 'Latvia'],
    ['LY', '218', 'Libya'],
    ['MA', '212', 'Morocco'],
    ['MC', '377', 'Monaco'],
    ['MD', '373', 'Moldova'],
    ['ME', '382', 'Montenegro'],
    ['MF', '590', 'Saint Martin (French part)'],
    ['MG', '261', 'Madagascar'],
    ['MH', '692', 'Marshall Islands'],
    ['MK', '389', 'North Macedonia'],
    ['ML', '223', 'Mali'],
    ['MM', '95', 'Myanmar'],
    ['MN', '976', 'Mongolia'],
    ['MO', '853', 'Macao'],
    ['MP', '1', 'Northern Mariana Islands'],
    ['MQ', '596', 'Martinique'],
    ['MR', '222', 'Mauritania'],
    ['MS', '1', 'Montserrat'],
    ['MT', '356', 'Malta'],
    ['MU', '230', 'Mauritius'],
    ['MV', '960', 'Maldives'],
    ['MW', '265', 'Malawi'],
    ['MX', '52', 'Mexico'],
    ['MY', '60', 'Malaysia'],
    ['MZ', '258', 'Mozambique'],
    ['NA', '264', 'Namibia'],
    ['NC', '687', 'New Caledonia'],
    ['NE', '227', 'Niger'],
    ['NF', '672', 'Norfolk Island'],
    ['NG', '234', 'Nigeria'],
    ['NI', '505', 'Nicaragua'],
    ['NL', '31', 'Netherlands'],
    ['NO', '47', 'Norway'],
    ['NP', '977', 'Nepal'],
    ['NR', '674', 'Nauru'],
    ['NU', '683', 'Niue'],
    ['NZ', '64', 'New Zealand'],
    ['OM', '968', 'Oman'],
    ['PA', '507', 'Panama'],
    ['PE', '51', 'Peru'],
    ['PF', '689', 'French Polynesia'],
    ['PG', '675', 'Papua New Guinea'],
    ['PH', '63', 'Philippines'],
    ['PK', '92', 'Pakistan'],
    ['PL', '48', 'Poland'],
    ['PM', '508', 'Saint Pierre and Miquelon'],
    ['PN', '64', 'Pitcairn'],
    ['PR', '1', 'Puerto Rico'],
    ['PS', '970', 'Palestine, State of'],
    ['PT', '351', 'Portugal'],
    ['PW', '680', 'Palau'],
    ['PY', '595', 'Paraguay'],
    ['QA', '974', 'Qatar'],
    ['RE', '262', 'Réunion'],
    ['RO', '40', 'Romania'],
    ['RS', '381', 'Serbia'],
    ['RU', '7', 'Russian Federation'],
    ['RW', '250', 'Rwanda'],
    ['SA', '966', 'Saudi Arabia'],
    ['SB', '677', 'Solomon Islands'],
    ['SC', '248', 'Seychelles'],
    ['SD', '249', 'Sudan'],
    ['SE', '46', 'Sweden'],
    ['SG', '65', 'Singapore'],
    ['SH', '290', 'Saint Helena, Ascension and Tristan da Cunha'],
    ['SI', '386', 'Slovenia'],
    ['SJ', '47', 'Svalbard and Jan Mayen'],
    ['SK', '421', 'Slovakia'],
    ['SL', '232', 'Sierra Leone'],
    ['SM', '378', 'San Marino'],
    ['SN', '221', 'Senegal'],
    ['SO', '252', 'Somalia'],
    ['SR', '597', 'Suriname'],
    ['SS', '211', 'South Sudan'],
    ['ST', '239', 'Sao Tome and Principe'],
    ['SV', '503', 'El Salvador'],
    ['SX', '1', 'Sint Maarten (Dutch part)'],
    ['SY', '963', 'Syrian Arab Republic'],
    ['SZ', '268', 'Eswatini'],
    ['TC', '1', 'Turks and Caicos Islands'],
    ['TD', '235', 'Chad'],
    ['TF', '262', 'French Southern Territories'],
    ['TG', '228', 'Togo'],
    ['TH', '66', 'Thailand'],
    ['TJ', '992', 'Tajikistan'],
    ['TK', '690', 'Tokelau'],
    ['TL', '670', 'Timor-Leste'],
    ['TM', '993', 'Turkmenistan'],
    ['TN', '216', 'Tunisia'],
    ['TO', '676', 'Tonga'],
    ['TR', '90', 'Türkiye'],
    ['TT', '1', 'Trinidad and Tobago'],
    ['TV', '688', 'Tuvalu'],
    ['TW', '886', 'Taiwan'],
    ['TZ', '255', 'Tanzania'],
    ['UA', '380', 'Ukraine'],
    ['UG', '256', 'Uganda'],
    ['UM', '1', 'United States Minor Outlying Islands'],
    ['US', '1', 'United States'],
    ['UY', '598', 'Uruguay'],
    ['UZ', '998', 'Uzbekistan'],
    ['VA', '39', 'Holy See'],
    ['VC', '1', 'Saint Vincent and the Grenadines'],
    ['VE', '58', 'Venezuela'],
    ['VG', '1', 'Virgin Islands, British'],
    ['VI', '1', 'Virgin Islands, U.S.'],
    ['VN', '84', 'Viet Nam'],
    ['VU', '678', 'Vanuatu'],
    ['WF', '681', 'Wallis and Futuna'],
    ['WS', '685', 'Samoa'],
    ['YE', '967', 'Yemen'],
    ['YT', '262', 'Mayotte'],
    ['ZA', '27', 'South Africa'],
    ['ZM', '260', 'Zambia'],
    ['ZW', '263', 'Zimbabwe'],
];
/** Every country, ordered by ISO code. */
export const COUNTRIES = COUNTRY_TABLE.map(([code, dialCode, name]) => ({
    code,
    dialCode,
    name,
}));
const BY_CODE = new Map(COUNTRIES.map((country) => [country.code, country]));
export function isCountryCode(value) {
    return BY_CODE.has(value);
}
/**
 * Total lookup — `CountryCode` is derived from the same table, so the miss
 * branch is unreachable from typed callers and exists only to keep the return
 * type non-nullable for them.
 */
export function countryFor(code) {
    const found = BY_CODE.get(code);
    if (found === undefined) {
        throw new Error(`countryFor: unknown ISO 3166-1 alpha-2 code "${code}"`);
    }
    return found;
}
/**
 * Which country a shared dial code DISPLAYS as.
 *
 * A dial code is not a country: +1 is 25 NANP territories, +44 is the UK plus
 * three Crown Dependencies, +7 is Russia and Kazakhstan. E.164 alone cannot tell
 * them apart without a national numbering plan, which this module deliberately
 * does not carry — so parsing picks the declared representative and says so.
 * The EMITTED value is unaffected: the digits round-trip byte-for-byte, only the
 * flag beside them is a best guess. An explicit pick by the user still wins, and
 * `PhoneInput` keeps it (see its `country` derivation).
 *
 * Every dial code with more than one country must appear here — `countries.test.ts`
 * fails if one is added without a representative.
 */
const PRIMARY_BY_DIAL_CODE = {
    '1': 'US',
    '7': 'RU',
    '39': 'IT',
    '44': 'GB',
    '47': 'NO',
    '61': 'AU',
    '64': 'NZ',
    '212': 'MA',
    '262': 'RE',
    '358': 'FI',
    '500': 'FK',
    '590': 'GP',
    '599': 'CW',
    '672': 'NF',
};
const PRIMARY_BY_DIAL = (() => {
    const map = new Map();
    for (const country of COUNTRIES) {
        const existing = map.get(country.dialCode);
        if (existing === undefined || PRIMARY_BY_DIAL_CODE[country.dialCode] === country.code) {
            map.set(country.dialCode, country);
        }
    }
    return map;
})();
/** Dial codes longest first — E.164 has no separator, so parsing is longest-prefix. */
export const DIAL_CODES_LONGEST_FIRST = [...PRIMARY_BY_DIAL.keys()].sort((a, b) => b.length - a.length || a.localeCompare(b));
/** The country a bare dial code displays as, or `null` when no country dials it. */
export function primaryCountryForDialCode(dialCode) {
    return PRIMARY_BY_DIAL.get(dialCode) ?? null;
}
/** Every country sharing a dial code, in ISO order. Exported for tests and callers that
 *  want to explain the ambiguity in their own UI. */
export function countriesForDialCode(dialCode) {
    return COUNTRIES.filter((country) => country.dialCode === dialCode);
}
const REGIONAL_INDICATOR_A = 0x1f1e6;
const LATIN_CAPITAL_A = 0x41;
/**
 * The country's flag as two Unicode regional-indicator code points — no asset,
 * no icon font, no dependency. Platforms without flag glyphs (Windows Chrome)
 * render the two letters instead, which is still the ISO code, so it degrades
 * into information rather than into tofu.
 */
export function countryFlagEmoji(code) {
    return [...code]
        .map((letter) => String.fromCodePoint(REGIONAL_INDICATOR_A + ((letter.codePointAt(0) ?? 0) - LATIN_CAPITAL_A)))
        .join('');
}
//# sourceMappingURL=countries.js.map